import { useCallback, useEffect, useRef, useState } from "react";

const PEEK = 68;

function sizes(available) {
  const full = Math.max(PEEK + 120, available - 10);
  return { peek: PEEK, half: Math.round(Math.min(full, Math.max(320, available * 0.55))), full };
}

// Hoja inferior para móvil con tres posiciones: asomada, media y completa.
// Se mide sobre el contenedor real (no sobre el viewport visual), así el teclado,
// la barra del navegador o el zoom no la dejan con un tamaño desfasado.
export default function useSheet(containerRef, initial = "peek") {
  const [snap, setSnap] = useState(initial);
  const [dragHeight, setDragHeight] = useState(null);
  const [available, setAvailable] = useState(() => window.innerHeight - 56);
  const gesture = useRef(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;
    const update = () => { if (element.clientHeight > 0) setAvailable(element.clientHeight); };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [containerRef]);

  const heights = sizes(available);
  const visible = Math.min(heights.full, dragHeight ?? heights[snap]);

  const cycle = useCallback(() => setSnap((current) => (current === "peek" ? "half" : current === "half" ? "full" : "half")), []);

  const finish = () => {
    const state = gesture.current;
    gesture.current = null;
    if (!state) return;
    if (!state.moved) { cycle(); return; }
    const projected = (dragHeight ?? visible) + state.velocity * 180;
    const nearest = Object.entries(heights).sort((left, right) => Math.abs(left[1] - projected) - Math.abs(right[1] - projected))[0][0];
    setSnap(nearest);
    setDragHeight(null);
  };

  const onPointerDown = (event) => {
    if (event.button !== 0 && event.pointerType === "mouse") return;
    gesture.current = { startY: event.clientY, startHeight: visible, lastY: event.clientY, lastTime: event.timeStamp, velocity: 0, moved: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event) => {
    const state = gesture.current;
    if (!state) return;
    const delta = state.startY - event.clientY;
    if (Math.abs(delta) > 6) state.moved = true;
    if (!state.moved) return;
    const elapsed = Math.max(1, event.timeStamp - state.lastTime);
    state.velocity = (state.lastY - event.clientY) / elapsed;
    state.lastY = event.clientY;
    state.lastTime = event.timeStamp;
    setDragHeight(Math.min(heights.full, Math.max(heights.peek, state.startHeight + delta)));
  };

  return {
    snap,
    setSnap,
    visible,
    halfHeight: heights.half,
    dragging: dragHeight != null,
    style: { height: `${heights.full}px`, transform: `translate3d(0, ${heights.full - visible}px, 0)`, "--sheet-visible": `${visible}px` },
    gripProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: finish,
      onPointerCancel: finish,
      onLostPointerCapture: finish,
      onKeyDown: (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); cycle(); } },
    },
  };
}
